import api from './api.service';

export interface SecurityScan {
  id: string;
  tenant_id: string;
  name: string;
  description?: string;
  type: 'sast' | 'dast' | 'pentest' | 'dependency' | 'ssl' | 'headers' | 'port' | 'dns';
  target: string;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
  progress: number;
  config?: Record<string, any>;
  started_at?: string;
  completed_at?: string;
  duration_seconds?: number;
  findings_count: number;
  critical_count: number;
  high_count: number;
  medium_count: number;
  low_count: number;
  info_count: number;
  security_score?: number;
  error_message?: string;
  summary?: Record<string, any>;
  created_by?: string;
  is_scheduled: boolean;
  schedule_cron?: string;
  created_at: string;
  updated_at: string;
}

export interface SecurityFinding {
  id: string;
  scan_id: string;
  tenant_id: string;
  severity: 'critical' | 'high' | 'medium' | 'low' | 'info';
  title: string;
  description: string;
  category: string;
  cwe_id?: string;
  cve_id?: string;
  cvss_score?: number;
  affected_url?: string;
  affected_component?: string;
  affected_file?: string;
  affected_line?: number;
  evidence?: string;
  request?: string;
  response?: string;
  remediation: string;
  references?: string[];
  status: 'open' | 'confirmed' | 'false_positive' | 'fixed' | 'accepted';
  notes?: string;
  assigned_to?: string;
  fixed_at?: string;
  created_at: string;
}

export interface SecurityRule {
  id: string;
  tenant_id?: string;
  rule_id: string;
  name: string;
  description: string;
  scan_type: string;
  default_severity: string;
  category: string;
  cwe_id?: string;
  pattern?: string;
  config?: Record<string, any>;
  is_enabled: boolean;
  is_custom: boolean;
  created_at: string;
}

export interface DashboardSummary {
  totalScans: number;
  runningScans: number;
  completedScans: number;
  totalFindings: number;
  openFindings: number;
  criticalFindings: number;
  highFindings: number;
  avgSecurityScore: number;
  recentScans: SecurityScan[];
  findingsBySeverity: Record<string, number>;
}

class SecurityScanService {
  private baseUrl = '/v1/security';

  async getDashboard(): Promise<{ success: boolean; data: DashboardSummary }> {
    return api.get(`${this.baseUrl}/dashboard`);
  }

  async getAll(type?: string, limit: number = 50): Promise<{ success: boolean; data: SecurityScan[] }> {
    return api.get(this.baseUrl, { params: { type, limit } });
  }

  async getById(id: string): Promise<{ success: boolean; data: SecurityScan }> {
    return api.get(`${this.baseUrl}/${id}`);
  }

  async getFindings(scanId: string, severity?: string): Promise<{ success: boolean; data: SecurityFinding[] }> {
    return api.get(`${this.baseUrl}/${scanId}/findings`, { params: { severity } });
  }

  async startScan(type: string, target: string, config?: Record<string, any>): Promise<{ success: boolean; data: SecurityScan; message: string }> {
    return api.post(`${this.baseUrl}/scan`, { type, target, config });
  }

  async startQuickAnalysis(target: string): Promise<{ success: boolean; data: SecurityScan[]; message: string }> {
    return api.post(`${this.baseUrl}/quick-analysis`, { target });
  }

  async startPentest(target: string, config?: Record<string, any>): Promise<{ success: boolean; data: SecurityScan; message: string }> {
    return api.post(`${this.baseUrl}/pentest`, { target, config });
  }

  async cancel(id: string): Promise<{ success: boolean; message: string }> {
    return api.post(`${this.baseUrl}/${id}/cancel`);
  }

  async delete(id: string): Promise<{ success: boolean; message: string }> {
    return api.delete(`${this.baseUrl}/${id}`);
  }

  async getRules(): Promise<{ success: boolean; data: SecurityRule[] }> {
    return api.get(`${this.baseUrl}/rules/list`);
  }

  async createRule(data: Partial<SecurityRule>): Promise<{ success: boolean; data: SecurityRule }> {
    return api.post(`${this.baseUrl}/rules`, data);
  }

  async updateRule(id: string, data: Partial<SecurityRule>): Promise<{ success: boolean; data: SecurityRule }> {
    return api.put(`${this.baseUrl}/rules/${id}`, data);
  }

  async deleteRule(id: string): Promise<{ success: boolean; message: string }> {
    return api.delete(`${this.baseUrl}/rules/${id}`);
  }

  async exportPDF(scanId: string): Promise<Blob> {
    return api.get(`${this.baseUrl}/${scanId}/export/pdf`, {
      responseType: 'blob',
    });
  }

  async exportExcel(scanId: string): Promise<Blob> {
    return api.get(`${this.baseUrl}/${scanId}/export/excel`, {
      responseType: 'blob',
    });
  }

  downloadBlob(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }
}

export default new SecurityScanService();
